import React, { ChangeEvent } from "react";
import { Control, Controller, UseControllerProps } from "react-hook-form";
import { FlexColumn } from "../Common/__styles__/Layout";
import { PrintOnly } from "../Common/__styles__/PrintOnly";
import { ScreenOnly } from "../Common/__styles__/ScreenOnly";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import {
  CheckboxWrapper,
  CustomCheckbox,
  HiddenInput,
  LabelRow,
  Wrapper,
} from "./__styles__/Checkbox";

interface GenericProps {
  name?: string;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  additionalOnChange?: (evt: ChangeEvent<HTMLInputElement>) => void;
}

export type CheckboxProps = GenericProps & {
  value?: boolean | "indeterminate";
  onChange: (value: boolean) => void;
};

const Checkbox = ({
  value = false,
  name,
  onChange,
  additionalOnChange,
  disabled = false,
  readonly = false,
  label,
  description,
  error,
  required,
}: CheckboxProps) => {
  const _handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    evt.stopPropagation();
    onChange(evt.target.checked);
    if (additionalOnChange) {
      additionalOnChange(evt);
    }
  };

  const isChecked = value === true;
  const isIndeterminate = value === "indeterminate";

  return (
    <>
      <ScreenOnly>
        <Wrapper
          data-testid="checkbox"
          disabled={disabled}
          readonly={readonly}
          checked={isChecked}
        >
          <HiddenInput
            type="checkbox"
            checked={isChecked}
            onChange={_handleChange}
            disabled={disabled || readonly}
            id={name}
            ref={input => {
              if (input) {
                input.indeterminate = isIndeterminate;
              }
            }}
          />
          <CheckboxWrapper disabled={disabled || readonly}>
            <CustomCheckbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              error={!!error}
            >
              {isChecked && (
                <Icon
                  iconName="check"
                  color="contentPrimaryInverted"
                  size={12}
                />
              )}
              {isIndeterminate && (
                <Icon
                  iconName="minus"
                  color="contentPrimaryInverted"
                  size={12}
                />
              )}
            </CustomCheckbox>
          </CheckboxWrapper>

          {label && (
            <FlexColumn>
              <LabelRow>
                <Body type="regular" size="default" as="div">
                  {label}
                </Body>
                {required && (
                  <Body
                    type="regular"
                    size="default"
                    color="contentCritical"
                    as="div"
                  >
                    *
                  </Body>
                )}
              </LabelRow>
              {description && (
                <Body
                  type="regular"
                  size="default"
                  color="contentSecondary"
                  as="div"
                >
                  {description}
                </Body>
              )}
            </FlexColumn>
          )}
        </Wrapper>
      </ScreenOnly>
      <PrintOnly flexRow data-testid={`print-only-${name}`}>
        <Body type="regular" size="default" as="div">
          {label ? `${label}: ` : ""}
          {value ? "Yes" : "No"}
        </Body>
      </PrintOnly>
    </>
  );
};

type ReactHookFormCheckboxProps = GenericProps &
  Omit<UseControllerProps, "control"> & { control: Control<any> };

export const ReactHookFormCheckbox = (props: ReactHookFormCheckboxProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={{
        required: props.required,
        ...props.rules,
      }}
      defaultValue={false}
      render={({ field, fieldState: { error } }) => {
        return (
          <Checkbox
            {...props}
            value={field.value ?? false}
            onChange={value => field.onChange(value)}
            error={error?.message}
          />
        );
      }}
    />
  );
};

export default Checkbox;
